import { AdminPaymentTransaction } from "../interfaces/payment.interface";

export interface AdminVerifyRequestBody {
  role: "Parent" | "Teacher";
  source: AdminPaymentTransaction["source"];
  itemType: string;
  userId: string;
  packageId?: string;
  shadowWalletId?: string;
  tuitionPeriodId?: string;
  noOfClasses?: number;
  isFullCancellationSubsidy?: boolean;
  certificateId?: string;
}

const VERIFIABLE_SOURCES = new Set<AdminPaymentTransaction["source"]>([
  "payment",
  "cac",
  "groupClass",
  "trialClass",
  "contactAccess",
]);

const VERIFIABLE_KEYS = new Set<string>([
  "payment|Parent|BUY_GURU_COIN_PACKAGE",
  "payment|Parent|BUY_SHADOW_WALLET_RECHARGE_PACK",
  "payment|Parent|BUY_CHAT_AND_CALL_PACKAGE",
  "payment|Parent|TUITION_REQUEST_BY_CASHFREE",
  "payment|Parent|coinEarnerParticipation",
  "payment|Teacher|teacherSubscription",
  "payment|Teacher|buyTeacherSubjectCertificate",
  "payment|Teacher|payoutProtector",
  "payment|Teacher|payoutProtectorCancellationSubsidy",
  "payment|Teacher|teacherTuitionCancellationFee",
  "payment|Teacher|sponsorTeacherProfile",
  "payment|Teacher|BUY_CHAT_AND_CALL_PACKAGE",
  "cac|Parent|BUY_CAC_PACKAGE",
  "cac|Teacher|BUY_CAC_PACKAGE",
  "groupClass|Teacher|groupClassSubscription",
  "trialClass|Parent|BUY_TRIAL_CLASS_PACKAGE",
  "contactAccess|Parent|CONTACT_ACCESS_PAYMENT",
  "contactAccess|Teacher|CONTACT_ACCESS_PAYMENT",
]);

function verifyKey(
  source: AdminPaymentTransaction["source"],
  role: AdminPaymentTransaction["role"],
  itemType: string
): string {
  return `${source}|${role}|${itemType}`;
}

function isCashfreePayment(row: AdminPaymentTransaction): boolean {
  const method = row.paymentMethod?.trim().toLowerCase();
  if (!method) return true;
  return method === "cashfree";
}

function mapItemTypeForRole(
  itemType: string,
  role: AdminPaymentTransaction["role"]
): string {
  if (role === "Parent") {
    if (itemType === "tuitionRequest") return "TUITION_REQUEST_BY_CASHFREE";
    if (itemType === "buyGuruCoins") return "BUY_GURU_COIN_PACKAGE";
    if (itemType === "coinEarnerParticipationCoins") {
      return "coinEarnerParticipation";
    }
  }
  return itemType;
}

export function getVerifyOrderId(row: AdminPaymentTransaction): string {
  return row.orderId || row._id;
}

export function canAdminVerifyTransaction(row: AdminPaymentTransaction): boolean {
  if (row.status !== "Pending") return false;
  if (!isCashfreePayment(row)) return false;
  if (!row.itemType) return false;
  if (!row.createdBy?._id) return false;
  if (!VERIFIABLE_SOURCES.has(row.source)) return false;
  const itemType = mapItemTypeForRole(row.itemType, row.role);
  return VERIFIABLE_KEYS.has(verifyKey(row.source, row.role, itemType));
}

export function buildAdminVerifyBody(
  row: AdminPaymentTransaction
): AdminVerifyRequestBody {
  const rawItemType = row.itemType!;
  const itemType = mapItemTypeForRole(rawItemType, row.role);
  const meta = (row.metadata ?? {}) as Record<string, unknown>;

  const body: AdminVerifyRequestBody = {
    role: row.role,
    source: row.source,
    itemType,
    userId: row.createdBy!._id,
  };

  if (
    (itemType === "BUY_CHAT_AND_CALL_PACKAGE" ||
      itemType === "BUY_TRIAL_CLASS_PACKAGE" ||
      itemType === "BUY_CAC_PACKAGE") &&
    row.packageId
  ) {
    body.packageId = row.packageId;
  }

  if (itemType === "BUY_SHADOW_WALLET_RECHARGE_PACK" && meta.shadowWalletId) {
    body.shadowWalletId = String(meta.shadowWalletId);
  }

  if (itemType === "payoutProtector") {
    if (meta.shadowWalletId) body.shadowWalletId = String(meta.shadowWalletId);
    if (meta.tuitionPeriodId) body.tuitionPeriodId = String(meta.tuitionPeriodId);
    if (meta.noOfClasses != null) body.noOfClasses = Number(meta.noOfClasses);
    if (meta.isFullCancellationSubsidy != null) {
      body.isFullCancellationSubsidy = Boolean(meta.isFullCancellationSubsidy);
    }
  }

  if (itemType === "payoutProtectorCancellationSubsidy") {
    if (meta.shadowWalletId) body.shadowWalletId = String(meta.shadowWalletId);
    if (meta.tuitionPeriodId) body.tuitionPeriodId = String(meta.tuitionPeriodId);
  }

  if (itemType === "teacherTuitionCancellationFee" && meta.tuitionPeriodId) {
    body.tuitionPeriodId = String(meta.tuitionPeriodId);
  }

  if (itemType === "buyTeacherSubjectCertificate") {
    const productInfo = meta.productInfo;
    if (
      productInfo &&
      typeof productInfo === "object" &&
      !Array.isArray(productInfo) &&
      (productInfo as { certificateId?: string }).certificateId
    ) {
      body.certificateId = (productInfo as { certificateId: string })
        .certificateId;
    }
  }

  return body;
}
